/* eslint-disable prettier/prettier */
import React, { useCallback, useEffect, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { Moment } from 'moment-timezone';
import moment from 'moment';
import jquery from 'jquery';

import { DATE_FORMATS } from 'utils/dateTime';
import pearClient from 'apis/pearClient';

import {
  CleanScreenResultType,
  DailyPatientUsageSummaryDto,
  VisitType,
} from '@peartherapeutics/hylia-api-client';
import styles from './styles.module.scss';

// TODO (JB): REPLACE WITH PROPER BACKEND ENUMS ONCE EXPORTED
enum MedicationComplianceValueEnum {
  Yes = 'Yes',
  No = 'No',
  NA = 'N/A',
}

enum PatientUsageValueEnum {
  Yes = 'Yes',
  No = 'No',
  NA = 'N/A',
}

interface IProps {
  dailySummaries: DailyPatientUsageSummaryDto[];
  updateMedicationCompliances: (selectedDateCalendar: Moment) => void;
  showBup: boolean;
}

const CustomCalendar = (props: IProps) => {
  const calendarRef = useRef<any>(null);
  const { dailySummaries, updateMedicationCompliances, showBup } = props;

  const getCurrentMonthName = (): string => {
    const calendarApi = calendarRef.current!.getApi();
    const currentCalendarDate = calendarApi.getDate();
    return moment(currentCalendarDate).format('MMMM');
  };

  const trackChangeMonthEvent = useCallback((direction: string): void => {
    const monthName = getCurrentMonthName();
    pearClient.v2.tracking.trackChangeMonthOnCalendarTabEvent(
      direction,
      monthName,
    );
  }, []);

  const handleTodayButtonClicked = useCallback(() => {
    trackChangeMonthEvent('today');
  }, [trackChangeMonthEvent]);

  const handleNextButtonClicked = useCallback(() => {
    trackChangeMonthEvent('forward');
  }, [trackChangeMonthEvent]);

  const handlePreviousButtonClicked = useCallback(() => {
    trackChangeMonthEvent('backward');
  }, [trackChangeMonthEvent]);

  const generateApptComplianceElement = (date: Moment) => {
    const summary = dailySummaries.find(
      (summary) => summary.date === date.format(DATE_FORMATS.YYYY_MM_DD),
    );

    if (summary?.appointmentCompliance === VisitType.Attended)
      return `<div class=${styles['appt-compliant']}></div>`;
    if (summary?.appointmentCompliance === VisitType.Absent)
      return `<div class=${styles['appt-skipped']}></div>`;
    return '';
  };

  const generateCleanScreenElement = (date: Moment) => {
    const summary = dailySummaries.find(
      (summary) => summary.date === date.format(DATE_FORMATS.YYYY_MM_DD),
    );

    if (summary?.cleanScreenCompliance === CleanScreenResultType.Positive)
      return `<div class=${styles['clean-screen-pos']}></div>`;
    if (summary?.cleanScreenCompliance === CleanScreenResultType.Negative)
      return `<div class=${styles['clean-screen-neg']}></div>`;
    return '';
  };

  const generatePatientUsageElement = (date: Moment) => {
    const summary = dailySummaries.find(
      (summary) => summary.date === date.format(DATE_FORMATS.YYYY_MM_DD),
    );

    // TODO (JB): WORK WITH BACKEND TO EXPORT PROPER ENUMS
    if (summary?.substanceUse === MedicationComplianceValueEnum.Yes)
      return `<div class=${styles['report-used']}></div>`;
    if (summary?.substanceUse === MedicationComplianceValueEnum.No)
      return `<div class=${styles['report-nonuse']}></div>`;
    return '';
  };

  const generateReportedBupUseElement = (date: Moment) => {
    if (!showBup) {
      return '';
    }

    const summary = dailySummaries.find(
      (summary) => summary.date === date.format(DATE_FORMATS.YYYY_MM_DD),
    );

    if (summary?.medicationCompliance === PatientUsageValueEnum.Yes)
      return `<div class=${styles['bup-used']}></div>`;
    if (summary?.medicationCompliance === PatientUsageValueEnum.No)
      return `<div class=${styles['bup-nonuse']}></div>`;
    return '';
  };

  const renderDay = (dateObj: any) => {
    const renderingDate = dateObj.date;
    const renderingMomentDate = moment(renderingDate);
    if (!calendarRef.current) {
      return {
        html: `<div class=${styles['icon-positioner']}>
                <div class=${
                  styles['date-number']
                }>${renderingDate.getDate()}</div></div>`,
      };
    }
    const month = renderingDate.getMonth();
    const calendarApi = calendarRef.current!.getApi();
    const currentCalendarDate = calendarApi.getDate();
    if (currentCalendarDate.getMonth() === month) {
      const apptCompliance = generateApptComplianceElement(renderingMomentDate);
      const cleanScreen = generateCleanScreenElement(renderingMomentDate);
      const patientUsage = generatePatientUsageElement(renderingMomentDate);
      const bupUsage = generateReportedBupUseElement(renderingMomentDate);
      return {
        html: `
            <div class=${styles['icon-positioner']}>
              <div class=${
                styles['date-number']
              }>${renderingDate.getDate()}</div>
              <div class=${styles['substance-icons']}>
                ${bupUsage}${patientUsage}${apptCompliance}${cleanScreen}
              </div>
            </div>
        `,
      };
    }
    return {
      html: `<div class=${styles['icon-positioner']}>
    <div class=${styles['date-number']}>${renderingDate.getDate()}</div></div>`,
    };
  };

  const renderView = (dateInfo: any) => {
    const today = new Date();
    const title = dateInfo.view.title;
    const currentCalendarMonth = title.split(' ')[0];
    const currentCalendarYear = title.split(' ')[1];
    const fullCalendarNextButton = document.getElementsByClassName(
      'fc-next-button',
    )[0] as HTMLButtonElement;
    const currentMonth = moment(today, DATE_FORMATS.MMMM_YYYY, true).format(
      'MMMM',
    );
    const currentYear = moment(today, DATE_FORMATS.MMMM_YYYY, true).year();
    const isCurrentMonth =
      currentCalendarMonth === currentMonth &&
      currentCalendarYear === currentYear.toString();
    if (isCurrentMonth) {
      fullCalendarNextButton.disabled = true;
    } else {
      fullCalendarNextButton.disabled = false;
    }

    if (dateInfo.view.start > today) {
      calendarRef.current?.getApi().gotoDate(today.toISOString());
    }

    const selectedDateCalendar = moment(title, DATE_FORMATS.MMMM_YYYY);
    updateMedicationCompliances(selectedDateCalendar);
  };

  useEffect(() => {
    jquery('.fc-today-button').on('click', handleTodayButtonClicked);
    jquery('.fc-next-button').on('click', handleNextButtonClicked);
    jquery('.fc-prev-button').on('click', handlePreviousButtonClicked);
  }, [
    handleNextButtonClicked,
    handlePreviousButtonClicked,
    handleTodayButtonClicked,
  ]);

  return (
    <FullCalendar
      ref={calendarRef}
      plugins={[dayGridPlugin]}
      initialView="dayGridMonth"
      dayCellContent={renderDay}
      datesSet={renderView}
      headerToolbar={{
        start: '',
        center: 'prev,title,next,today',
        end: '',
      }}
      dayHeaderFormat={{ weekday: 'long' }}
      fixedWeekCount={false}
      showNonCurrentDates={false}
      height={'450px'}
      dayHeaderClassNames={'calendar-header'}
    />
  );
};

export default CustomCalendar;
